export type AttendanceUser = {
  uid: string;
  name: string;
  email: string;
  joined: boolean;
};

export type AttendanceRoom = {
  id: string;
  name: string;
  createdBy: string;
  startAt: Date | null;
  participants: string[];
};

export type RoomAttendance = {
  roomId: string;
  name: string;
  startAt: Date | null;
  ownerName: string;
  signedUp: AttendanceUser[];
  walkIns: AttendanceUser[];
  joinedCount: number;
  noShowCount: number;
  // Scheduled but not yet happened. Nobody can be a no-show for it, so the
  // count is held at 0 rather than reporting every sign-up as a failure.
  isUpcoming: boolean;
};

export type UserLookup = Record<string, { name?: string; email?: string }>;

const resolve = (
  uid: string,
  usersById: UserLookup,
  joined: boolean,
): AttendanceUser => {
  const user = usersById[uid];
  return {
    uid,
    name: user?.name?.trim() || "Unknown user",
    email: user?.email?.trim() || "",
    joined,
  };
};

/**
 * Works out who signed up for a room and who actually turned up.
 *
 * "Signed up" comes from room.participants. "Joined" comes from the room's
 * presenceEvents (type "joined") — not participantsActions, which only exists
 * from 2026-07-08 and would make every older room look like a no-show.
 */
export function buildRoomAttendance(input: {
  room: AttendanceRoom;
  joinedUids: string[];
  usersById: UserLookup;
  now?: Date;
}): RoomAttendance {
  const { room, joinedUids, usersById, now = new Date() } = input;
  const isUpcoming = room.startAt
    ? room.startAt.getTime() > now.getTime()
    : false;

  // A user can join, leave and rejoin — presenceEvents records each one.
  const joined = new Set(joinedUids);

  const signedUp = room.participants.map((uid) =>
    resolve(uid, usersById, joined.has(uid)),
  );

  const signedUpUids = new Set(room.participants);
  const walkIns = Array.from(joined)
    .filter((uid) => !signedUpUids.has(uid))
    .map((uid) => resolve(uid, usersById, true));

  return {
    roomId: room.id,
    name: room.name.trim() || "Untitled conversation",
    startAt: room.startAt,
    ownerName: usersById[room.createdBy]?.name?.trim() || "Unknown user",
    signedUp,
    walkIns,
    joinedCount: signedUp.filter((user) => user.joined).length,
    noShowCount: isUpcoming
      ? 0
      : signedUp.filter((user) => !user.joined).length,
    isUpcoming,
  };
}
